import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import IconHighlight from '../../../IconHighlight.jsx';
import { toneFor } from '../constants/statTones';
import { enterAt, formatRatio, formatValue, WidgetPeriodChip } from '../shared';

/** `trend` → the tone its delta prints in. Absent means no verdict. */
const TONE_BY_TREND = Object.freeze({ UP: 'success', DOWN: 'critical', FLAT: 'neutral' });

/** What an item with nothing to compare against says, rather than nothing. */
const NO_COMPARISON = 'no comparison';

/**
 * Related counters, side by side — `METRIC_ROW`.
 *
 * Two arrangements for the same several-numbers-read-together shape:
 *
 * - `divided` (default) — no boxes, a hairline between stats. This is what
 *   `display.item_density: INLINE` asks for, and it is the right default: the
 *   items are facets of one subject, and giving each its own bordered card
 *   states that they are separate things.
 * - `card` — each stat in its own box with an icon. For a row that genuinely
 *   is a set of states: Normal / Warnings / Critical.
 *
 * ## The delta is the point (CT-5641)
 *
 * `items[]` carries `trend` and `delta_pct` beside each value, and without
 * them a metric row is three numbers with no sense of direction — which is
 * most of what the type is for. Each item prints its own, coloured by its
 * `trend`, and an item with nothing to compare against says so rather than
 * leaving a gap the reader has to interpret.
 *
 * The icon only appears in the `card` arrangement, and only when the item has
 * a tone. A neutral ⓘ on every stat is furniture: it takes the eye first and
 * says nothing, where the delta's own colour says the thing worth knowing.
 *
 * `COMPOSITE_METRIC` used to render here too, as a third variant. It does not
 * any more — see `CompositeMetricWidget`. Its numbers are a result and the
 * parts it was computed from, and a row of equal boxes states the opposite:
 * that they are peers.
 */
const fromItems = (items) => items.map((item) => {
  const hasDelta = item.delta_pct !== null && item.delta_pct !== undefined;
  const against = item.comparison_label ?? item.comparison_period ?? '';

  return {
    key: item.key ?? item.label,
    label: item.label ?? item.key ?? '',
    value: formatValue(item.value, item.format),
    detail: hasDelta
      ? `${formatRatio(item.delta_pct)}${against ? ` vs ${against}` : ''}`
      : NO_COMPARISON,
    muted: !hasDelta,
    tone: TONE_BY_TREND[item.trend] ?? 'neutral',
    hasTone: Boolean(item.trend),
  };
});

function StatRowWidget({ data, variant = 'divided', className = '' }) {
  const stats = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.items)) return fromItems(data.items);
    // The pre-v2 shape, so a caller that still passes `stats` keeps working.
    return (data.stats ?? []).map((stat) => ({
      key: stat.label,
      label: stat.label,
      value: formatValue(stat.value, stat.format),
      detail: null,
      muted: false,
      tone: stat.tone ?? 'neutral',
      hasTone: Boolean(stat.tone),
      icon: stat.icon,
    }));
  }, [data]);

  if (!stats.length) return null;

  const divided = variant === 'divided';

  return (
    <div className={`flex flex-col gap-2 ${className}`.trim()}>
      <div className="flex gap-2">
        {stats.map((stat, index) => {
          const tone = toneFor(stat.tone);
          const enter = enterAt(index, 'left');
          // Only where it carries a state; see the note above.
          const Icon = divided ? null : (stat.icon ?? (stat.hasTone ? tone.defaultIcon : null));

          const shell = divided
            ? ['flex flex-1 flex-col gap-0.5', index > 0 ? 'border-l border-edge pl-2.5' : ''].join(' ')
            : 'flex flex-1 items-start gap-3 rounded-lg border border-edge bg-surface px-4 py-3';

          return (
            <div key={stat.key} className={`${shell} ${enter.className}`} style={enter.style}>
              {Icon ? (
                <IconHighlight icon={Icon} bgColor={tone.bg} iconColor={tone.icon} size="small" />
              ) : null}
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[10px] font-medium leading-tight text-content-muted">
                  {stat.label}
                </span>
                <span className="text-sm font-semibold leading-none text-content">
                  {stat.value}
                </span>
                {stat.detail ? (
                  <span
                    className={`truncate text-[10px] font-medium leading-tight ${
                      stat.muted ? 'italic text-content-faint' : tone.text
                    }`}
                  >
                    {stat.detail}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <WidgetPeriodChip period={data?.period} className="self-start" />
    </div>
  );
}

StatRowWidget.propTypes = {
  /** `METRIC_ROW`'s `items[]`. */
  data: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.number,
      format: PropTypes.object,
      trend: PropTypes.oneOf(['UP', 'DOWN', 'FLAT', null]),
      delta_pct: PropTypes.number,
      comparison_value: PropTypes.number,
      /** What the delta is measured against, e.g. `Sep`. */
      comparison_label: PropTypes.string,
    })),
    period: PropTypes.object,
    /** The pre-v2 shape, still accepted — see the branch in the body. */
    stats: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  variant: PropTypes.oneOf(['card', 'divided']),
  className: PropTypes.string,
};

export default memo(StatRowWidget);
